import React, { useState, useEffect } from 'react'

import { toast } from 'react-toastify'

import HomeDoctor from '../../../../../presentation/pages/doctors/home'

import { useFetchContracts } from '../../../../../presentation/hooks/fetch-contracts'
import { useFirstAccess } from '../../../../../presentation/hooks/first-access'
import { useMe } from '../../../../../presentation/contexts/me'
import useNotificationPolling from '../../../../../presentation/hooks/notification-polling'
import useNewMessageNotification from '../../../../../presentation/hooks/new-message-notification'
import useFetchRanking from '../../../../../presentation/hooks/fetch-ranking'
import useFetchConversations from '../../../../../presentation/hooks/fetch-conversations'

import { makeDoctorsContracts } from '../../../../usecases/contracts/remote-contracts-factory'
import { makeRequests } from '../../../../usecases/requests/remote-requests-factory'
import { makeUsers } from '../../../../usecases/users/remote-users-factory'
import { makeDoctor } from '../../../../usecases/doctors/remote-doctors-factory'

import IContractedContractor from '../../../../../presentation/interfaces/contracted-contractor'

import isHttpError from '../../../../../presentation/helpers/helper-http-error'
import HelperNotificationToken from '../../../../../presentation/helpers/helper-notification-token'

const MakeHomeDoctor = () => {
  document.title = 'BuscarMédicos - Início'

  const [requests, setRequests] = useState<any[]>([])

  const { me } = useMe()

  const [user, setUser] = useState({
    firstName: '',
    document: [
      {
        id: 0,
        url: '',
      },
    ],
    status: true,
  })

  const [available, setAvailable] = useState(user.status)

  const { isModalOpen, handleModalClose } = useFirstAccess()

  const { contracts } = useFetchContracts<IContractedContractor>(
    makeDoctorsContracts(me?.id as number),
  )

  const hasNewNotification = useNotificationPolling()

  const hasNewMessage = useNewMessageNotification(me?.id)

  const rankingData = useFetchRanking()

  const conversations = useFetchConversations(me?.id)

  useEffect(() => {
    const fetchRequests = async () => {
      if (me) {
        try {
          const data = await makeRequests(
            `/solicitations/${me?.id}/doctors`,
          ).load()

          setRequests(data)
        } catch (error) {
          const httpError = isHttpError(error)
          if (httpError) {
            toast.error(`Erro ao buscar as solicitações: ${httpError.message}`)
          } else {
            throw error
          }
        }
      }
    }

    fetchRequests()
  }, [me])

  useEffect(() => {
    const fetchMe = async () => {
      try {
        if (me) {
          const response = await makeUsers().loadDoctorById(me.id)
          setUser(response)
          setAvailable(response.status)
        }
      } catch (error) {
        const httpError = isHttpError(error)
        if (httpError) {
          toast.error(`Erro ao buscar o usuário: ${httpError.message}`)
        } else {
          throw error
        }
      }
    }

    fetchMe()
  }, [me])

  useEffect(() => {
    const requestPermission = async () => {
      await HelperNotificationToken.requestPermission()
    }

    requestPermission()
  }, [])

  const handleSwitch = async () => {
    try {
      const status = !available
      if (me) {
        await makeDoctor().editStatus(me.id, status)
      }

      setAvailable(status)
    } catch (error: any) {
      toast.error(`Erro ao alterar o status: ${error.message}`)
    }
  }

  const limitedContractorData = contracts
    .map((contractor) => ({
      ...contractor,
      chatId: contractor.contractorId,
      hasNewMessage: conversations.some(
        (conversation) => conversation.id === String(contractor.contractorId)
          && conversation.messages.some(
            (message) => !message.isRead && message.receiverId === String(me?.id),
          ),
      ),
    }))
    .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
    .slice(0, 5)

  const recentRequests = requests
    .map((contractor) => ({
      ...contractor,
      chatId: contractor.contractorId,
      hasNewMessage: conversations.some(
        (conversation) => conversation.id === String(contractor.contractorId)
          && conversation.messages.some(
            (message) => !message.isRead && message.receiverId === String(me?.id),
          ),
      ),
    }))
    .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
    .slice(0, 5)

  const homeProps = {
    setAvailable: handleSwitch,
    available,
    tableDataAll: limitedContractorData,
    isModalOpen,
    handleModalClose,
    recentRequests,
    hasNewNotification,
    hasNewMessage,
    rankingData,
    imgUrl: user.document.find((doc) => doc.url.startsWith('images/'))?.url,
    name: user.firstName,
  }

  return <HomeDoctor {...homeProps} />
}

export default MakeHomeDoctor
